import { Button } from '@snsw/react-component-library';
import {
  AutoSuggestAddress,
  AutoSuggestAddressModel,
  AutoSuggestAddressProps,
} from 'components/Address/AutoSuggestAddress';
import { OverseasAddress, OverseasAddressModel } from 'components/Address/OverseasAddress';
import { Box } from 'components/Layout';
import { ReactNode, useRef, useState } from 'react';
import { GlobalAddressEntryMode, GlobalAddressModel } from './types';

type Props = {
  /** Unique ID used to prefix the form fields */
  id: string;

  /** Label to show above the input component */
  label: ReactNode;

  helpMessage?: ReactNode;

  initialMode?: GlobalAddressEntryMode;

  value?: GlobalAddressModel;
  onChange: (value: GlobalAddressModel) => void;

  localAddressProps: Pick<
    AutoSuggestAddressProps,
    'onSelect' | 'onBlur' | 'api' | 'addressType' | 'minSearchLength' | 'debounceMillis' | 'manualAddressProps'
  >;
};

const useModel = (
  value: GlobalAddressModel
): [GlobalAddressModel, (next: Partial<GlobalAddressModel>) => GlobalAddressModel] => {
  // Store a ref that contains both the local and international entered data, so we can repopulate the control if the
  // user flips between local and international. We're using a ref here because we don't want to trigger a rerender
  // when we update this value - the rerender will already trigger because we are calling `onChange` on the host.
  const ref = useRef<GlobalAddressModel>(value);

  const updateModel = (next: GlobalAddressModel): GlobalAddressModel => (ref.current = { ...ref.current, ...next });

  return [ref.current, updateModel];
};

export const GlobalAddress = ({ id, label, helpMessage, initialMode, value, onChange, localAddressProps }: Props) => {
  const [entryMode, setEntryMode] = useState(initialMode ?? 'auto');

  const [model] = useModel(value);

  const handleLocalAddressChange = (local: AutoSuggestAddressModel) => onChange({ local });
  const handleOverseasAddressChange = (overseas: OverseasAddressModel) => onChange({ overseas });

  // The layout of this component follows the example given by Kiama
  // See: https://kiama.testservicensw.net/storybook/?path=/docs/international-address--default
  const isOverseas = entryMode === 'overseas';

  return (
    <>
      {isOverseas && (
        <>
          <Button onClick={() => setEntryMode('auto')} variant="link">
            Enter an Australian address
          </Button>
          <OverseasAddress
            id={`${id}-international`}
            label={label}
            helpMessage={helpMessage}
            value={model.overseas}
            onChange={handleOverseasAddressChange}
          />
        </>
      )}

      {!isOverseas && (
        <>
          <AutoSuggestAddress
            id={`${id}-local`}
            label={label}
            helpMessage={helpMessage}
            value={model.local}
            {...localAddressProps}
            onChange={handleLocalAddressChange}
            onEntryModeChange={setEntryMode}
          />
          <Box mt={entryMode === 'manual' ? 2 : 0.5}>
            <Button variant="link" onClick={() => setEntryMode('overseas')}>
              Enter an overseas address
            </Button>
          </Box>
        </>
      )}
    </>
  );
};
